// Single-chokepoint IPFS image URL helpers shared by web and mobile.
//
// All Morfi IPFS pins are reachable through both the public Pinata gateway
// (`gateway.pinata.cloud/ipfs/<cid>`) and our dedicated Pinata-backed gateway
// (`images.morfi.markets/ipfs/<cid>`) — the bytes are byte-identical because
// CIDs are content-addressed. We force every browser fetch through the
// dedicated gateway so traffic counts against our paid plan (higher rate
// limits, our CDN, our analytics) instead of the shared public endpoint.
//
// Apply at the boundary where a URL string is passed to an image component or
// `fetch()`. Pass-through for non-Pinata URLs (Google avatars, fal.media,
// data:, blob:, file:) so this is safe to call broadly.

const CUSTOM_GATEWAY_HOST = 'images.morfi.markets';
const PUBLIC_PINATA_HOST = 'gateway.pinata.cloud';

/** Origin of the dedicated IPFS gateway. Use to warm the connection
 *  (`preconnect`) before a streamed image URL arrives. */
export const CUSTOM_GATEWAY_ORIGIN = `https://${CUSTOM_GATEWAY_HOST}`;

export function toCustomGateway(url: string): string {
  if (url.length === 0) return url;
  // Cheap startsWith check before regex to avoid scanning every blob:/data:
  // URL we render. The rewrite only fires for the exact public Pinata host.
  if (!url.includes(PUBLIC_PINATA_HOST)) return url;
  return url.replace(
    /^https?:\/\/gateway\.pinata\.cloud\/ipfs\//i,
    `https://${CUSTOM_GATEWAY_HOST}/ipfs/`,
  );
}

export interface IpfsImageSize {
  /** Max rendered width in device pixels; the gateway never upscales. */
  width: number;
  /** Max rendered height in device pixels. */
  height?: number;
  /** Crop to fill both dimensions without enlarging smaller originals. */
  fit?: 'scale-down' | 'crop';
  /** Edge to preserve when cropping; the gateway centers by default. */
  gravity?: 'top' | 'bottom' | 'left' | 'right';
  /** Re-encode format; omit to keep the source format family. */
  format?: 'webp' | 'jpeg';
}

/**
 * Resize (and optionally re-encode) an IPFS image at the gateway edge via
 * Pinata image optimizations. AI proposal images are pinned as full-resolution
 * originals (MOR-2292), so each surface asks for the size it renders instead
 * of downloading the original. Non-gateway URLs pass through unchanged.
 */
export function ipfsImageUrl(url: string, size: IpfsImageSize): string {
  const gatewayUrl = toCustomGateway(url);
  if (!gatewayUrl.startsWith(`${CUSTOM_GATEWAY_ORIGIN}/ipfs/`)) return url;
  const parsed = new URL(gatewayUrl);
  for (const key of [...parsed.searchParams.keys()]) {
    if (key.startsWith('img-')) parsed.searchParams.delete(key);
  }
  parsed.searchParams.set('img-width', String(size.width));
  if (size.height !== undefined) parsed.searchParams.set('img-height', String(size.height));
  parsed.searchParams.set('img-fit', size.fit ?? 'scale-down');
  if (size.gravity !== undefined) parsed.searchParams.set('img-gravity', size.gravity);
  if (size.format !== undefined) parsed.searchParams.set('img-format', size.format);
  return parsed.toString();
}

export function toCustomGatewayOrNull(url: string | null): string | null {
  if (url === null) return null;
  return toCustomGateway(url);
}
